<script setup lang="ts">
import AnyIcon from '@/core/elements/AnyIcon.vue';
import IconButton from '@/core/elements/IconButton.vue';
import { useDebounce } from '@vueuse/core';
import { EIcon } from 'common/enum/misc.enum';
import { computed, nextTick, onMounted, ref, useId, useTemplateRef, watch } from 'vue';

const inputDomId = useId();
const inputRef = useTemplateRef<HTMLInputElement>('inputRef');

const props = withDefaults(
  defineProps<{
    autofocus?: boolean;
    disabled?: boolean;
    iconName?: EIcon;
    label?: string;
    modelValue: string;
    minLength?: number;
    placeholder?: string;
    size?: 'sm' | 'md';
    submitOnEnter?: boolean;
    autocomplete?: string;
  }>(),
  {
    autofocus: false,
    iconName: EIcon.SEARCH,
    label: undefined,
    minLength: 0,
    placeholder: undefined,
    size: 'md',
    submitOnEnter: false,
    autocomplete: 'on',
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const modelValueInternal = ref('');
const debouncedModelValue = useDebounce(modelValueInternal, 300);
const isFocused = ref(false);

const isLabelFloating = computed(() => {
  return isFocused.value || modelValueInternal.value.length > 0;
});

const reset = (): void => {
  modelValueInternal.value = '';
  emit('update:modelValue', modelValueInternal.value);
};

const focus = async (): Promise<void> => {
  await nextTick();
  inputRef.value?.focus();
};

const handleEnter = (e: KeyboardEvent): void => {
  if (e.isComposing) return;
  if (!props.submitOnEnter) e.preventDefault();
};

watch(debouncedModelValue, () => {
  const value = debouncedModelValue.value;
  emit('update:modelValue', value.length >= props.minLength ? value : '');
});

const showLengthHint = computed<boolean>(() => {
  return modelValueInternal.value.length < props.minLength && modelValueInternal.value.length > 0;
});

watch(
  () => props.modelValue,
  (modelValue) => {
    // Keep below-minimum text visible when the emitted empty value resets the parent filter.
    if (modelValue === '' && showLengthHint.value) return;
    modelValueInternal.value = modelValue;
  },
  {
    immediate: true,
  },
);

watch(
  () => props.autofocus,
  (autofocus) => {
    if (autofocus) void focus();
  },
);

onMounted(() => {
  if (props.autofocus) void focus();
});

defineExpose({
  focus,
});
</script>

<template>
  <div
    class="flex items-center gap-sm px-md group p-0"
    :class="[
      'input-text',
      isFocused || modelValueInternal
        ? 'outline outline-2 outline-tint outline-offset-[-2px]'
        : 'border-1 border-quaternary',
      disabled && 'input-text-disabled',
    ]"
  >
    <AnyIcon
      :icon-name="iconName"
      class="size-icon text-secondary shrink-0"
      :class="[showLengthHint ? 'group-focus-within:text-error' : 'group-focus-within:text-tint']"
    />
    <div class="relative flex flex-col gap-xs flex-1 min-w-0">
      <label
        v-if="label"
        :for="inputDomId"
        class="input-text-label left-0 block truncate max-w-full"
        :class="{
          'label-floating': isLabelFloating && size !== 'sm',
          '!max-w-[125%]': isLabelFloating && size !== 'sm',
          'opacity-0': size === 'sm' && isLabelFloating,
          'text-tint': isFocused,
        }"
      >
        {{ label }}
      </label>
      <input
        :id="inputDomId"
        ref="inputRef"
        v-model="modelValueInternal"
        :autofocus="autofocus"
        :placeholder="isLabelFloating ? placeholder : ''"
        :disabled="disabled"
        class="w-full text-ellipsis bg-transparent flex-grow border-none outline-none"
        :class="{ 'input-text-labeled': label, '!py-none !min-h-auto': size === 'sm' }"
        :autocomplete="autocomplete"
        @keydown.esc="reset()"
        @focus="isFocused = true"
        @blur="isFocused = false"
        @keydown.enter="handleEnter"
      />
    </div>
    <IconButton v-if="modelValueInternal" :icon-name="EIcon.X" type="button" theme="primary" @click="reset()" />
  </div>
</template>
